import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  Box,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
  Stack,
} from '@mui/material'
import React, { ChangeEvent, DragEvent, useCallback, useRef, useState } from 'react'
import { isVideoFile } from '@/components/inventory/utils/inventoryMedia'



interface UploadPhotosTabProps {
  onSubmitAddedPhotos: (stagedPhotos: { file: File; previewBlobUrl: string }[]) => void
  isMobile?: boolean
}

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png']
const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
]
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.mp4', '.webm', '.mov', '.m4v', '.mkv']

const isAcceptedUpload = (file: File): boolean => {
  const type = file.type.toLowerCase()
  if (type && (ACCEPTED_IMAGE_TYPES.includes(type) || ACCEPTED_VIDEO_TYPES.includes(type))) {
    return true
  }
  if (isVideoFile(file)) return true
  const lowerName = file.name.toLowerCase()
  return ACCEPTED_EXTENSIONS.some(ext => lowerName.endsWith(ext))
}

const DESKTOP_ACCEPT =
  '.jpg,.jpeg,.png,.mp4,.webm,.mov,.m4v,.mkv,video/*,image/jpeg,image/png'
const MOBILE_ACCEPT = 'image/*,video/*'

const UploadPhotosTab: React.FC<UploadPhotosTabProps> = ({ onSubmitAddedPhotos, isMobile = false }) => {
  const [stagedPhotos, setStagedPhotos] = useState<{ file: File; previewBlobUrl: string }[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)












  const addFiles = useCallback((files: File[]) => {


    const filtered = files.filter(isAcceptedUpload)
    if (filtered.length === 0) return

    const filesWithPreview = filtered.map(file => ({
      file,
      previewBlobUrl: URL.createObjectURL(file),
    }))

    setStagedPhotos(prev => [...prev, ...filesWithPreview])
  }, [])

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return
    }
    addFiles(Array.from(e.target.files))
    e.target.value = ''
  }

  const handleRemoveStagedPhoto = (index: number) => {


    setStagedPhotos(prev => {
      const target = prev[index]
      if (target) {
        URL.revokeObjectURL(target.previewBlobUrl)
      }
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleSubmitAddedPhotos = () => {
    onSubmitAddedPhotos(stagedPhotos)
    setStagedPhotos([])







  }

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragging(false)

    if (!e.dataTransfer?.files || e.dataTransfer.files.length === 0) return
    addFiles(Array.from(e.dataTransfer.files))
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: isDragging ? 'action.hover' : 'background.default',
            transition: theme.transitions.create(['border-color', 'background-color'], {
              duration: theme.transitions.duration.shortest,
            }),
            textAlign: 'center',
          })}
        >
          <CloudUploadIcon
            color={isDragging ? 'primary' : 'action'}
            sx={{ fontSize: 48 }}
          />
          <Typography variant='subtitle1'>
            {isDragging ? 'Drop files to add them' : 'Drag & drop photos or videos here'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            JPG, PNG, MP4, WebM, MOV &middot; or use the button below
          </Typography>

          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
            justifyContent='center'
            sx={{ mt: 1 }}
          >
            <Button
              component='label'
              variant='outlined'
              color='primary'
              startIcon={<UploadFileIcon />}

            >
              Select photos or videos
              <input
                type='file'
                accept={isMobile ? MOBILE_ACCEPT : DESKTOP_ACCEPT}
                multiple
                hidden
                onChange={handleFileUpload}
              />
            </Button>

            {stagedPhotos.length > 0 && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleSubmitAddedPhotos}
                startIcon={<SendIcon />}

              >
                Upload {stagedPhotos.length} file{stagedPhotos.length === 1 ? '' : 's'}


              </Button>
            )}
          </Stack>
        </Box>

        {stagedPhotos.length > 0 && (
          <Typography variant='body2' align='center' color='text.secondary'>
            {stagedPhotos.length} file{stagedPhotos.length === 1 ? '' : 's'} selected and ready to upload
          </Typography>
        )}
      </Box>

      <ImageList sx={{ width: 500, height: 450 }} cols={3} rowHeight={164}>
        {stagedPhotos && stagedPhotos.length > 0 && (
          <>
            {stagedPhotos.map(({ file, previewBlobUrl }, index) => (
              <ImageListItem key={index}>
                {isVideoFile(file) ? (
                  <video
                    src={previewBlobUrl}
                    muted
                    playsInline
                    preload='metadata'
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <img
                    src={previewBlobUrl}
                    alt={file.name}
                    loading='lazy'
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
                <ImageListItemBar
                  position='top'
                  actionIcon={
                    <IconButton
                      sx={{ color: 'red', background: 'black', padding: '2px' }}
                      aria-label={`close ${file.name}`}
                      onClick={() => handleRemoveStagedPhoto(index)}

                    >
                      <CloseIcon />
                    </IconButton>
                  }
                  actionPosition='right'
                  sx={{ background: 'transparent', padding: '4px' }}
                />
              </ImageListItem>
            ))}
          </>
        )}
      </ImageList>
    </>
  )
}

export default UploadPhotosTab